/**
 * Menu Component - Component Definition
 * =======================================
 *
 * Definición del componente Angular y registro del módulo.
 */

import angular, { IModule, IComponentOptions } from 'angular';
import 'angular-ui-bootstrap';

import { MenuController, MenuItemController } from './controller';
import { MENU_TEMPLATE, MENU_DROPDOWN_TEMPLATE, MENU_NAVBAR_TEMPLATE, MENU_ITEM_TEMPLATE } from './template';
import {
  VARIANTS,
  SIZES,
  ICONS,
  DEFAULTS,
  MenuVariant,
  MenuSize,
  MenuOrientation,
  MenuItem,
  MenuIconType
} from './constants';

// ============================================================================
// TYPES
// ============================================================================

export interface IMenuComponentBindings {
  items: MenuItem[];
  variant?: MenuVariant;
  size?: MenuSize;
  orientation?: MenuOrientation;
  collapsible?: boolean;
  showIcons?: boolean;
  activeItemId?: string;
  onItemClick?: (args: { item: MenuItem }) => void;
  // Fix (bug de interfaz detectado en la migración a compound): onItemHover
  // ya existía como binding registrado en `menuComponentDefinition` pero
  // faltaba en esta interfaz.
  onItemHover?: (args: { item: MenuItem }) => void;
}

// ============================================================================
// DEFINICIÓN DEL COMPONENTE
// ============================================================================

/**
 * Componente ecMenu - Menú vertical/horizontal
 */
export const menuComponentDefinition: IComponentOptions = {
  template: MENU_TEMPLATE,
  controller: MenuController,
  // transclude: true habilita el modo compound (ver ecMenuItem más abajo).
  // Sin items/ecMenuItem hijos, comportamiento actual intacto (cero breaking).
  transclude: true,
  bindings: {
    items: '<',
    variant: '@',
    size: '@',
    orientation: '@',
    collapsible: '<',
    showIcons: '<',
    activeItemId: '@',
    onItemClick: '&?',
    onItemHover: '&?'
  }
};

/**
 * Componente ecMenuDropdown - Menú desplegable
 */
export const menuDropdownComponentDefinition: IComponentOptions = {
  template: MENU_DROPDOWN_TEMPLATE,
  controller: MenuController,
  bindings: {
    items: '<',
    triggerText: '@',
    variant: '@',
    size: '@',
    showIcons: '<',
    onItemClick: '&?'
  }
};

/**
 * Componente ecMenuNavbar - Barra de navegación horizontal
 */
export const menuNavbarComponentDefinition: IComponentOptions = {
  template: MENU_NAVBAR_TEMPLATE,
  controller: MenuController,
  transclude: {
    brand: '?menuBrand',
    actions: '?menuActions'
  },
  bindings: {
    items: '<',
    variant: '@',
    size: '@',
    showIcons: '<',
    activeItemId: '@',
    onItemClick: '&?'
  }
};

// ============================================================================
// DEFINICIÓN DE HIJO COMPOUND
// ============================================================================

/**
 * Componente ecMenuItem — hijo compound de ecMenu (composición vía
 * transclusión + auto-registro). require opcional (`^?ecMenu`) + guard en
 * MenuItemController: ver "Mecánica común" del patrón compound.
 */
export const menuItemComponentDefinition: IComponentOptions = {
  template: MENU_ITEM_TEMPLATE,
  controller: MenuItemController,
  require: { menu: '^?ecMenu' },
  transclude: true,
  bindings: {
    itemId: '@',
    icon: '@',
    badge: '@',
    disabled: '<',
    onClick: '&?'
  }
};

// ============================================================================
// REGISTRO DEL MÓDULO ANGULAR
// ============================================================================

export const MODULE_NAME = 'ec.menu';

const menuModule: IModule = angular
  .module(MODULE_NAME, ['ui.bootstrap', 'ngSanitize'])
  .component('ecMenu', menuComponentDefinition)
  .component('ecMenuDropdown', menuDropdownComponentDefinition)
  .component('ecMenuNavbar', menuNavbarComponentDefinition)
  .component('ecMenuItem', menuItemComponentDefinition);

// ============================================================================
// EXPORTS
// ============================================================================

export {
  MenuController,
  MenuItemController,
  MENU_TEMPLATE,
  MENU_DROPDOWN_TEMPLATE,
  MENU_NAVBAR_TEMPLATE,
  MENU_ITEM_TEMPLATE,
  VARIANTS,
  SIZES,
  ICONS,
  DEFAULTS
};

export type {
  MenuVariant,
  MenuSize,
  MenuOrientation,
  MenuItem,
  MenuIconType
} from './constants';

export default menuModule;
